/**
 * Builder Core 2.0 — The Mother Module
 * Shared infrastructure for all product-type builders.
 * Contains: State Manager, Pricing Engine, Cart Integration, Event Bus, DOM Utilities, Fractions.
 * ZERO product-specific code. Exposed as window.BuilderCore.
 */
(function() {
  'use strict';

  var BuilderCore = {};

  /* ============================================================
     STATE MANAGER
     Plain observer pattern. set() triggers all subscribers.
     ============================================================ */
  BuilderCore.createStore = function(initialState) {
    var state = {};
    var keys = Object.keys(initialState);
    for (var i = 0; i < keys.length; i++) {
      state[keys[i]] = initialState[keys[i]];
    }
    var subscribers = [];

    return {
      get: function(key) {
        if (key === undefined) {
          var copy = {};
          var k = Object.keys(state);
          for (var i = 0; i < k.length; i++) {
            copy[k[i]] = state[k[i]];
          }
          return copy;
        }
        return state[key];
      },

      set: function(key, value) {
        var oldValue = state[key];
        state[key] = value;
        if (oldValue !== value) {
          for (var i = 0; i < subscribers.length; i++) {
            try {
              subscribers[i](key, value, state);
            } catch (e) {
              console.error('[BuilderCore] Subscriber error:', e);
            }
          }
        }
      },

      subscribe: function(fn) {
        subscribers.push(fn);
        return function unsubscribe() {
          var idx = subscribers.indexOf(fn);
          if (idx > -1) subscribers.splice(idx, 1);
        };
      }
    };
  };


  /* ============================================================
     PRICING ENGINE
     3-layer dynamic pricing:
       Layer 1: color group → WxH price matrix
       Layer 2: color group → width/height range
       Layer 3: width → addon price (valance/fascia)
     ============================================================ */
  BuilderCore.PricingEngine = function(pricingData) {
    this.data = pricingData;
    this.activeGroupId = null;
    this._activeGroup = null;
  };

  var PE = BuilderCore.PricingEngine.prototype;

  /**
   * Set the active color group (triggered when customer picks a color).
   * @param {string} groupId - ID from color_groups array
   */
  PE.setActiveGroup = function(groupId) {
    this.activeGroupId = groupId;
    this._activeGroup = null;
    if (!this.data || !this.data.color_groups) return;
    for (var i = 0; i < this.data.color_groups.length; i++) {
      if (this.data.color_groups[i].id === groupId) {
        this._activeGroup = this.data.color_groups[i];
        break;
      }
    }
  };

  /**
   * Get the currently active color group object.
   * @returns {object|null}
   */
  PE.getActiveGroup = function() {
    return this._activeGroup;
  };

  /**
   * Round a value UP to the next pricing tier.
   * e.g. value=30, tiers=[24,36,48] → { index: 1, tier: 36 }
   * e.g. value=36, tiers=[24,36,48] → { index: 1, tier: 36 }
   * e.g. value=49, tiers=[24,36,48] → null (exceeds max)
   * @param {number} value
   * @param {number[]} tiers - sorted ascending
   * @returns {{ index: number, tier: number }|null}
   */
  PE.roundUpToTier = function(value, tiers) {
    if (!tiers || tiers.length === 0) return null;
    for (var i = 0; i < tiers.length; i++) {
      if (value <= tiers[i]) {
        return { index: i, tier: tiers[i] };
      }
    }
    return null; // exceeds max tier
  };

  /**
   * Layer 1: Get base price from WxH matrix for the active color group.
   * Rounds up both width and height to nearest pricing tier.
   * @param {number} width - actual width in inches (can include fractions)
   * @param {number} height - actual height in inches
   * @returns {number|null} price in dollars, or null if invalid
   */
  PE.getBasePrice = function(width, height) {
    var group = this._activeGroup;
    if (!group) return null;
    // price_tiers can be inside the color group OR at root level
    var tiers = group.price_tiers || this.data.price_tiers;
    if (!tiers) return null;

    var wResult = this.roundUpToTier(width, tiers.widths);
    var hResult = this.roundUpToTier(height, tiers.heights);

    if (!wResult || !hResult) return null;
    if (!tiers.prices || !tiers.prices[hResult.index]) return null;

    return tiers.prices[hResult.index][wResult.index] || null;
  };

  /**
   * Layer 2: Get width/height range for the active color group.
   * @returns {{ minWidth: number, maxWidth: number, minHeight: number, maxHeight: number }|null}
   */
  PE.getWidthRange = function() {
    var group = this._activeGroup;
    if (!group) return null;
    // price_tiers can be inside the color group OR at root level
    var tiers = group.price_tiers || this.data.price_tiers;
    var defaultMinW = (tiers && tiers.widths && tiers.widths.length > 0) ? tiers.widths[0] : 24;
    var defaultMinH = (tiers && tiers.heights && tiers.heights.length > 0) ? tiers.heights[0] : 36;
    var defaultMaxW = (tiers && tiers.widths && tiers.widths.length > 0) ? tiers.widths[tiers.widths.length - 1] : 96;
    var defaultMaxH = (tiers && tiers.heights && tiers.heights.length > 0) ? tiers.heights[tiers.heights.length - 1] : 96;
    return {
      minWidth: group.min_width != null ? group.min_width : defaultMinW,
      maxWidth: group.max_width != null ? group.max_width : defaultMaxW,
      minHeight: group.min_height != null ? group.min_height : defaultMinH,
      maxHeight: group.max_height != null ? group.max_height : defaultMaxH
    };
  };

  /**
   * Layer 3: Get addon price based on width (for valance/fascia).
   * Rounds up width to nearest tier.
   * @param {string} addonType - key in data.addons (e.g. "cassette", "fascia")
   * @param {number} width - actual width in inches
   * @returns {number} price in dollars, or 0 if not found
   */
  PE.getAddonPrice = function(addonType, width) {
    // Look up the addon table in `addons` first, then `valance_pricing`.
    // Checking per-key (not the whole object) lets one parent carry BOTH a
    // control-band `addons` table and a `valance_pricing` table without the
    // addons object short-circuiting valance lookups (e.g. a Roman parent with
    // banded controls AND a cassette valance).
    var d = this.data;
    if (!d) return 0;
    var addon = (d.addons && d.addons[addonType]) || (d.valance_pricing && d.valance_pricing[addonType]);
    if (!addon) return 0;

    // Format A: { widths: [24,36,...], prices: [44,52.75,...] } (array-based)
    if (addon.widths && addon.prices) {
      var wResult = this.roundUpToTier(width, addon.widths);
      if (!wResult) return 0;
      return addon.prices[wResult.index] || 0;
    }

    // Format B: { "24": 44, "36": 52.75, ... } (object-based, keys are width strings)
    var keys = Object.keys(addon).map(function(k) { return parseInt(k, 10); }).filter(function(k) { return !isNaN(k); });
    keys.sort(function(a, b) { return a - b; });
    var wResult = this.roundUpToTier(width, keys);
    if (!wResult) return 0;
    return addon[String(wResult.tier)] || 0;
  };

  /**
   * Get a flat surcharge value by key.
   * @param {string} key - key in data.surcharges
   * @returns {number} price in dollars, or 0
   */
  PE.getSurcharge = function(key) {
    if (!this.data || !this.data.surcharges) return 0;
    return this.data.surcharges[key] || 0;
  };

  /**
   * Get oversize surcharge if width exceeds the product's oversize threshold.
   * Pricing JSON: "oversize": { "threshold": 96, "surcharge": 100 }
   * @param {number} width - actual width in inches
   * @returns {number} surcharge in dollars, or 0
   */
  PE.getOversizeSurcharge = function(width) {
    if (!this.data || !this.data.oversize) return 0;
    var os = this.data.oversize;
    var threshold = os.threshold || os.threshold_width;
    if (!threshold || !os.surcharge) return 0;
    return width > threshold ? os.surcharge : 0;
  };

  /**
   * Calculate total price from all selections.
   * @param {object} selections - { basePrice, controlSurcharge, valancePrice, oversizeSurcharge }
   * @returns {number} total in dollars
   */
  PE.calculateTotal = function(selections) {
    var total = (selections.basePrice || 0)
      + (selections.controlSurcharge || 0)
      + (selections.valancePrice || 0)
      + (selections.oversizeSurcharge || 0);
    return Math.round(total * 100) / 100; // avoid floating point drift
  };


  /* ============================================================
     CART INTEGRATION
     Async fetch()-based bundle add/remove.
     Sequential Promise chain — children first, parent last.
     ============================================================ */
  BuilderCore.Cart = {};

  /**
   * Generate a unique bundle timestamp.
   * Uses crypto.randomUUID() with Date.now() fallback.
   * @returns {string}
   */
  BuilderCore.Cart.generateTimestamp = function() {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
    return String(Date.now());
  };

  /**
   * Add a single item to cart via /cart/add.js
   * @param {number} variantId
   * @param {number} quantity
   * @param {object} properties
   * @returns {Promise}
   */
  BuilderCore.Cart._addItem = function(variantId, quantity, properties) {
    return fetch('/cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        id: variantId,
        quantity: quantity,
        properties: properties
      })
    }).then(function(response) {
      if (!response.ok) {
        // Try JSON error, fall back to status text if response isn't JSON (e.g. 500 HTML page)
        return response.text().then(function(text) {
          var msg = 'Failed to add item (HTTP ' + response.status + ')';
          try {
            var parsed = JSON.parse(text);
            if (parsed.description) msg = parsed.description;
          } catch (e) { /* not JSON — use default msg */ }
          throw new Error(msg);
        });
      }
      return response.json();
    });
  };

  /**
   * Add a bundle (parent + children) to cart.
   * Uses Shopify's batch /cart/add.js endpoint (items array) for a
   * single network request instead of sequential calls per item.
   * Children are listed first (reversed order), parent last.
   * All items share the same _Timestamp.
   *
   * @param {object} parentItem - { variantId, quantity, properties }
   * @param {Array} childItems - [{ variantId, quantity, properties }, ...]
   * @param {object} [options] - { redirect: boolean }
   * @returns {Promise}
   */
  BuilderCore.Cart.addBundle = function(parentItem, childItems, options) {
    var opts = options || { redirect: true };

    // Build a single items array: children (reversed) first, parent last
    var items = [];
    var reversedChildren = childItems.slice().reverse();
    for (var i = 0; i < reversedChildren.length; i++) {
      var child = reversedChildren[i];
      items.push({
        id: child.variantId,
        quantity: child.quantity,
        properties: child.properties
      });
    }
    items.push({
      id: parentItem.variantId,
      quantity: parentItem.quantity,
      properties: parentItem.properties
    });

    // Standalone addon (e.g. MeasureSafe) goes in the same batch request.
    // It's NOT part of _BundleIds but ships in one round-trip.
    if (opts.addonItem) {
      items.push({
        id: opts.addonItem.variantId,
        quantity: opts.addonItem.quantity,
        properties: opts.addonItem.properties
      });
    }

    // Single fetch call with all items
    return fetch('/cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: items })
    }).then(function(response) {
      if (!response.ok) {
        return response.text().then(function(text) {
          var msg = 'Failed to add items (HTTP ' + response.status + ')';
          try {
            var parsed = JSON.parse(text);
            if (parsed.description) msg = parsed.description;
          } catch (e) { /* not JSON */ }
          throw new Error(msg);
        });
      }
      return response.json();
    }).then(function() {
      if (opts.redirect) {
        window.location.href = '/cart';
      }
    }).catch(function(err) {
      console.error('[BuilderCore] Cart add failed:', err);
      throw err;
    });
  };

  /**
   * Remove all items with a given timestamp from cart.
   * @param {string} timestamp - _Timestamp value
   * @returns {Promise}
   */
  BuilderCore.Cart.removeBundle = function(timestamp) {
    return fetch('/cart.js', {
      credentials: 'same-origin'
    })
    .then(function(r) { return r.json(); })
    .then(function(cart) {
      var updates = {};
      cart.items.forEach(function(item) {
        if (item.properties && String(item.properties._Timestamp) === String(timestamp)) {
          updates[item.key] = 0;
        }
      });
      if (Object.keys(updates).length === 0) return;
      return fetch('/cart/update.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates: updates })
      });
    });
  };

  /**
   * Build _BundleIds CSV string.
   * @param {number} parentVariantId
   * @param {Array<number>} childVariantIds
   * @returns {string}
   */
  BuilderCore.Cart.buildBundleIds = function(parentVariantId, childVariantIds) {
    return [parentVariantId].concat(childVariantIds).join(', ');
  };


  /* ============================================================
     EVENT BUS
     Simple pub/sub for cross-module communication.
     ============================================================ */
  BuilderCore.events = {
    _listeners: {},

    on: function(event, fn) {
      if (!this._listeners[event]) this._listeners[event] = [];
      this._listeners[event].push(fn);
    },

    off: function(event, fn) {
      if (!this._listeners[event]) return;
      this._listeners[event] = this._listeners[event].filter(function(f) {
        return f !== fn;
      });
    },

    emit: function(event, data) {
      var fns = this._listeners[event];
      if (!fns) return;
      for (var i = 0; i < fns.length; i++) {
        try {
          fns[i](data);
        } catch (e) {
          console.error('[BuilderCore] Event handler error (' + event + '):', e);
        }
      }
    }
  };


  /* ============================================================
     DOM UTILITIES
     Thin wrappers — not jQuery replacements.
     ============================================================ */
  BuilderCore.dom = {
    qs: function(sel, ctx) {
      return (ctx || document).querySelector(sel);
    },

    qsa: function(sel, ctx) {
      return Array.prototype.slice.call((ctx || document).querySelectorAll(sel));
    },

    /**
     * Delegated event listener.
     * Usage: BuilderCore.dom.on(container, 'click', '.child-selector', handler)
     *    or: BuilderCore.dom.on(element, 'click', handler)
     */
    on: function(el, event, selectorOrFn, fn) {
      if (typeof selectorOrFn === 'function') {
        el.addEventListener(event, selectorOrFn);
        return;
      }
      var selector = selectorOrFn;
      el.addEventListener(event, function(e) {
        var target = e.target.closest(selector);
        if (target && el.contains(target)) {
          fn.call(target, e);
        }
      });
    },

    /**
     * Create an element with optional class and attributes.
     * @param {string} tag
     * @param {string} [className]
     * @param {object} [attrs]
     * @returns {HTMLElement}
     */
    create: function(tag, className, attrs) {
      var el = document.createElement(tag);
      if (className) el.className = className;
      if (attrs) {
        var keys = Object.keys(attrs);
        for (var i = 0; i < keys.length; i++) {
          el.setAttribute(keys[i], attrs[keys[i]]);
        }
      }
      return el;
    }
  };


  /* ============================================================
     FRACTION SUPPORT
     Width/height fraction handling for blinds measurements.
     ============================================================ */
  BuilderCore.fractions = {
    VALUES: [0, 0.125, 0.25, 0.375, 0.5, 0.625, 0.75, 0.875],
    LABELS: ['', '1/8"', '1/4"', '3/8"', '1/2"', '5/8"', '3/4"', '7/8"'],

    /**
     * Combine integer inches + fraction decimal.
     * @param {number} integer
     * @param {number} fractionDecimal
     * @returns {number}
     */
    combine: function(integer, fractionDecimal) {
      return (integer || 0) + (fractionDecimal || 0);
    },

    /**
     * Convert a fraction label to its decimal value.
     * @param {string} label - e.g. '1/4"'
     * @returns {number}
     */
    toDecimal: function(label) {
      var idx = this.LABELS.indexOf(label);
      return idx > -1 ? this.VALUES[idx] : 0;
    },

    /**
     * Convert a decimal to its fraction label.
     * @param {number} decimal
     * @returns {string}
     */
    toLabel: function(decimal) {
      var idx = this.VALUES.indexOf(decimal);
      return idx > -1 ? this.LABELS[idx] : '';
    },

    /**
     * Format a combined value for display (e.g. "30 1/4" or "45").
     * @param {number} integer
     * @param {number} fractionDecimal
     * @returns {string}
     */
    formatDisplay: function(integer, fractionDecimal) {
      var label = this.toLabel(fractionDecimal || 0);
      if (label) return integer + ' ' + label.replace('"', '') + '"';
      return integer + '"';
    }
  };


  /* ============================================================
     UTILITIES
     ============================================================ */

  /**
   * Format a number as USD currency string.
   * @param {number} amount - in dollars (e.g. 41.00)
   * @returns {string} e.g. "$41.00"
   */
  BuilderCore.formatMoney = function(amount) {
    var val = parseFloat(amount) || 0;
    return '$' + val.toFixed(2);
  };

  /**
   * Parse a JSON script tag by ID.
   * @param {string} id - element ID
   * @returns {object|null}
   */
  BuilderCore.parseJSON = function(id) {
    var el = document.getElementById(id);
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      console.error('[BuilderCore] Failed to parse #' + id + ':', e);
      return null;
    }
  };


  /* ============================================================
     SIZE LIMIT RULE
     ============================================================ */

  /**
   * Maximum drop for families whose limit depends on the fabric and the
   * options chosen, not on width alone.
   *
   * Narrow panels take a fixed length cap. Wider panels take an area cap,
   * so the limit falls as the panel widens. Splitting onto two headrails
   * halves the panel, which is why it is a real way out of a size limit.
   *
   * Config lives in builder_pricing.size_rule, so a family without that
   * key never reaches this function.
   *
   * @param {object} rule - { max_length, narrow_width_lt, panel_max_width,
   *                          caps: { fabric: { option: { length, sqft } } } }
   * @param {object} o - { width, fabric, option, panels }
   * @returns {object} ok:true with maxLength/panelWidth/basis/capSqft,
   *                   else ok:false with a reason.
   */
  BuilderCore.sizeLimit = function(rule, o) {
    if (!rule || !rule.caps) return { ok: false, reason: 'no_rule' };
    o = o || {};

    var width = parseFloat(o.width);
    if (!(width > 0)) return { ok: false, reason: 'no_width' };

    var panels = parseInt(o.panels, 10);
    if (!(panels >= 1)) panels = 1;
    // A 2-on-1 can be split unevenly, so an explicit widest-panel width wins
  // over the even split the panel count implies.
  var explicitPanel = parseFloat(o.panelWidth);
  var panelWidth = (explicitPanel > 0 && explicitPanel <= width) ? explicitPanel : (width / panels);

    var byFabric = rule.caps[o.fabric];
    if (!byFabric) return { ok: false, reason: 'unknown_fabric' };
    var cap = byFabric[o.option];
    if (!cap) return { ok: false, reason: 'unknown_option' };

    var panelMax = parseFloat(rule.panel_max_width);
    if (panelMax > 0 && panelWidth > panelMax) {
      return {
        ok: false,
        reason: 'panel_too_wide',
        panelWidth: panelWidth,
        panelMaxWidth: panelMax
      };
    }

    var hardMax = parseFloat(rule.max_length);
    if (!(hardMax > 0)) hardMax = Infinity;

    var narrowUnder = parseFloat(rule.narrow_width_lt);
    var maxLength, basis, capSqft = null;

    if (narrowUnder > 0 && panelWidth < narrowUnder) {
      maxLength = parseFloat(cap.length);
      basis = 'length';
    } else {
      capSqft = parseFloat(cap.sqft);
      if (!(capSqft > 0)) return { ok: false, reason: 'unknown_option' };
      maxLength = (capSqft * 144) / panelWidth;
      basis = 'sqft';
    }

    if (!(maxLength > 0)) return { ok: false, reason: 'unknown_option' };
    if (maxLength > hardMax) {
      maxLength = hardMax;
      basis = 'fabric';
      capSqft = null;
    }

    return {
      ok: true,
      maxLength: maxLength,
      panelWidth: panelWidth,
      basis: basis,
      capSqft: capSqft
    };
  };


  // Expose globally
  window.BuilderCore = BuilderCore;

})();
